import type { User, UserPermission } from "@/shared/types";
import {
  getRoleById,
  getRoleByName,
  normalizeRole,
  ROLES,
  type ApiRoleName,
  type UserRole,
  getPermissionId,
  getPermissionById,
  type PermissionName,
} from "@/shared/constants";

export interface ApiRole {
  id: number;
  name: string;
}

export interface ApiPermission {
  id: number;
  name: string;
}

export interface LoginApiUser {
  id: number;
  name: string;
  email: string;
  roles: ApiRole | ApiRole[] | null;
  permissions: Array<ApiPermission | string> | null;
  branch?: unknown;
  created_at: string;
  updated_at: string;
}

const LEGACY_ROLE_SLUGS: Record<string, UserRole> = {
  user: ROLES.BRANCH_STAFF,
  chief: ROLES.CK_STAFF,
};

const ROLE_ID_BY_SLUG: Record<UserRole, number> = {
  [ROLES.SUPER_ADMIN]: 1,
  [ROLES.ADMIN]: 2,
  [ROLES.BRANCH_MANAGER]: 3,
  [ROLES.BRANCH_STAFF]: 4,
  [ROLES.CK_MANAGER]: 5,
  [ROLES.CK_STAFF]: 6,
};

const isUserRole = (value: string): value is UserRole =>
  (Object.values(ROLES) as string[]).includes(value);

function toUserPermission(id: number, name?: string): UserPermission | undefined {
  if (Number.isNaN(id)) {
    return undefined;
  }

  const catalogEntry = getPermissionById(id);
  const resolvedName = name?.trim() || catalogEntry?.name;

  if (!resolvedName) {
    return undefined;
  }

  return { id, name: resolvedName };
}

function normalizePermissionEntry(permission: unknown): UserPermission | undefined {
  if (typeof permission === "number") {
    return toUserPermission(permission);
  }

  if (typeof permission === "string") {
    const trimmed = permission.trim();
    if (!trimmed) {
      return undefined;
    }

    const asNumber = Number(trimmed);
    if (!Number.isNaN(asNumber) && String(asNumber) === trimmed) {
      return toUserPermission(asNumber);
    }

    const id = getPermissionId(trimmed as PermissionName);
    if (id == null) {
      return undefined;
    }
    return { id, name: trimmed };
  }

  if (permission && typeof permission === "object") {
    if ("id" in permission) {
      const id = Number((permission as { id: unknown }).id);
      const name =
        "name" in permission && typeof (permission as { name: unknown }).name === "string"
          ? (permission as { name: string }).name
          : undefined;
      return toUserPermission(id, name);
    }
  }

  return undefined;
}

function dedupePermissions(permissions: UserPermission[]): UserPermission[] {
  const seen = new Set<number>();
  return permissions.filter((permission) => {
    if (seen.has(permission.id)) {
      return false;
    }
    seen.add(permission.id);
    return true;
  });
}

export function mapApiPermissions(
  permissions: LoginApiUser["permissions"],
): UserPermission[] {
  return dedupePermissions(
    (permissions ?? [])
      .map((permission) => normalizePermissionEntry(permission))
      .filter((permission): permission is UserPermission => permission != null),
  );
}

function normalizeStoredPermissions(raw: unknown): UserPermission[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return dedupePermissions(
    raw
      .map((permission) => normalizePermissionEntry(permission))
      .filter((permission): permission is UserPermission => permission != null),
  );
}

function getBranchId(branch: unknown): string | undefined {
  if (branch && typeof branch === "object" && "id" in branch) {
    return String((branch as { id: number }).id);
  }
  return undefined;
}

function getBranchName(branch: unknown): string | undefined {
  if (
    branch &&
    typeof branch === "object" &&
    "name" in branch &&
    typeof (branch as { name: unknown }).name === "string"
  ) {
    return (branch as { name: string }).name;
  }
  return undefined;
}

function resolveRoleFromStoredUser(stored: Partial<User>): UserRole {
  if (stored.roleName) {
    return normalizeRole(stored.roleName);
  }

  if (stored.role && isUserRole(stored.role)) {
    return stored.role;
  }

  if (stored.role && LEGACY_ROLE_SLUGS[stored.role]) {
    return LEGACY_ROLE_SLUGS[stored.role];
  }

  if (stored.roleId != null) {
    const apiRole = getRoleById(stored.roleId);
    if (apiRole) {
      return normalizeRole(apiRole.name);
    }
  }

  return ROLES.BRANCH_STAFF;
}

function resolveRoleName(stored: Partial<User>, role: UserRole): string {
  if (stored.roleName?.trim()) {
    return stored.roleName.trim();
  }

  if (stored.roleId != null) {
    const apiRole = getRoleById(stored.roleId);
    if (apiRole) {
      return apiRole.name;
    }
  }

  const displayNames: Record<UserRole, ApiRoleName> = {
    super_admin: "SuperAdmin",
    admin: "Admin",
    branch_manager: "branch-manager",
    branch_staff: "branch-staff",
    ck_manager: "ck-manager",
    ck_staff: "ck-staff",
  };

  return displayNames[role] ?? role.replace(/_/g, "-");
}

function resolveRoleId(stored: Partial<User>, role: UserRole): number | null {
  if (stored.roleId != null) {
    return stored.roleId;
  }

  if (stored.roleName) {
    const apiRole = getRoleByName(stored.roleName);
    if (apiRole) {
      return apiRole.id;
    }
  }

  return ROLE_ID_BY_SLUG[role] ?? null;
}

/** Maps login API user payload to the persisted frontend User shape. */
export function mapLoginUser(user: LoginApiUser): User {
  const firstRole = Array.isArray(user.roles) ? user.roles[0] : user.roles;
  const roleName = firstRole?.name ?? "";
  const role = normalizeRole(roleName);

  return {
    id: String(user.id),
    email: user.email,
    name: user.name,
    roleId: firstRole?.id ?? null,
    roleName,
    role,
    permissions: mapApiPermissions(user.permissions),
    branchId: getBranchId(user.branch),
    branchName: getBranchName(user.branch),
    createdAt: user.created_at,
    updatedAt: user.updated_at,
  };
}

/** Normalizes a user object loaded from localStorage (handles legacy shapes). */
export function normalizeStoredUser(raw: unknown): User | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const stored = raw as Partial<User>;
  if (!stored.id || !stored.email || !stored.name) {
    return null;
  }

  const role = resolveRoleFromStoredUser(stored);
  const roleName = resolveRoleName(stored, role);
  const roleId = resolveRoleId(stored, role);

  return {
    id: stored.id,
    email: stored.email,
    name: stored.name,
    avatar: stored.avatar,
    roleId,
    roleName,
    role,
    permissions: normalizeStoredPermissions(stored.permissions),
    branchId: stored.branchId,
    branchName: stored.branchName,
    createdAt: stored.createdAt ?? new Date().toISOString(),
    updatedAt: stored.updatedAt ?? new Date().toISOString(),
  };
}
